import { UserMapper } from "../user/user.mapper";
import { rateDiscriminator } from "./rate-discriminator.const";
import { LclRate } from "./rate-lcl.model";
import { Rate } from "./rate-type";
import { CarrierMapper } from "../carrier/carrier.mapper";
import { ClientMapper } from "../client/client.mapper";
import { FclRate } from "./rate-fcl.model";
import { RateLocationMapper } from "./rate-location/rate-location.mapper";
import { lclWeightUnit } from "./lcl-weight-unit.const";
import { ContainerPrices } from "../shared/container/container-prices.model";
import { AirRate } from "./rate-air.model";
import { FclFlatRate } from "./rate-fcl-flat.model";
import { RateFlatRouteMapper } from "./rate-flat-route-list/rate-flat-route.mapper";
import { AirFlatRate } from "./rate-air-flat.model";
import { LclFlatRate } from "./rate-lcl-flat.model";
import { LtlPickupDeliveryRate } from "./rate-ltl-pickup-delivery.model";
import { LtlRate } from "./rate-ltl.model";
import { LtlLinkedRate } from "./rate-ltl-linked.model";
import { WeightBracketPrices } from "../shared/weight-bracket/weight-bracket-prices.model";
import { LclWeightBracketPrices } from "../shared/lcl-weight-bracket/lcl-weight-bracket-prices.model";
import { FclPickupDeliveryRate } from "./rate-fcl-pickup-delivery.model";
import { AirWeightBracketPrices } from "app/shared/air-weight-bracket/air-weight-bracket-prices.model";
import { FtlFlatRate } from "./rate-ftl-flat.model";
import { PtlFlatRate } from "./rate-ptl-flat.model";
import { CompanyMapper } from "app/company/company.mapper";
import { RateNoteTranslation } from "./rate-note-translation.model";

export class RateMapper {
  public static map(data: any): Rate {
    const rate = RateMapper.mapByDiscriminator(data);

    if (rate) {
      rate.setExternalNoteTranslations(
        data.externalNoteTranslations &&
          Array.isArray(data.externalNoteTranslations)
          ? data.externalNoteTranslations.map(
              (translation) =>
                new RateNoteTranslation(
                  translation.locale,
                  translation.externalNote,
                ),
            )
          : [],
      );
      rate.setDefaultForInternalEmail(data.defaultForInternalEmail === true);
      rate.setDefaultForExternalEmail(data.defaultForExternalEmail === true);
      rate.setAvailableForPartner(data.availableForPartner === true);
      rate.setAgent(data.agent ? ClientMapper.map(data.agent) : null);
    }

    return rate;
  }

  private static mapByDiscriminator(data: any): Rate {
    if (!data) {
      return null;
    }

    if (!data.discriminator) {
      console.trace("no discriminator");
    }

    if (data.discriminator === rateDiscriminator.fcl) {
      return new FclRate(
        data.id,
        data.name,
        data.discriminator,
        new ContainerPrices(data.containers),
        new ContainerPrices(data.containerMargins),
        new ContainerPrices(data.totals),
        data.equipment ? data.equipment.map((eq) => String(eq)) : [],
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.currency,
        data.originLocations && !data.baseOriginLocation
          ? RateLocationMapper.map(data.originLocations[0])
          : data.originLocation
            ? RateLocationMapper.map(data.originLocation)
            : RateLocationMapper.map(data.baseOriginLocation) || null,
        data.destinationLocations && !data.baseDestinationLocation
          ? RateLocationMapper.map(data.destinationLocations[0])
          : data.destinationLocation
            ? RateLocationMapper.map(data.destinationLocation)
            : RateLocationMapper.map(data.baseDestinationLocation) || null,
        data.contractNumber,
        data.availableOnline,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.incoterms && Array.isArray(data.incoterms) ? data.incoterms : [],
      );
    }

    if (data.discriminator === rateDiscriminator.fclFlat) {
      return new FclFlatRate(
        data.id,
        data.name,
        data.discriminator,
        new ContainerPrices(data.containerMargins),
        data.equipment ? data.equipment.map((eq) => String(eq)) : [],
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.contractNumber,
        data.availableOnline,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.incoterms && Array.isArray(data.incoterms) ? data.incoterms : [],
        data.pickupDeliveryRate
          ? <FclPickupDeliveryRate>RateMapper.map(data.pickupDeliveryRate)
          : null,
        data.flatRateRoutes
          ? data.flatRateRoutes.map(RateFlatRouteMapper.map)
          : [],
        new ContainerPrices(data.totals),
      );
    }

    if (
      data.discriminator === rateDiscriminator.ftl ||
      data.discriminator === rateDiscriminator.ftlFlat
    ) {
      return new FtlFlatRate(
        data.id,
        data.name,
        data.discriminator,
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.currency,
        data.contractNumber,
        data.availableOnline,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.flatRateRoutes
          ? data.flatRateRoutes.map(RateFlatRouteMapper.map)
          : [],
        data.originLocation
          ? RateLocationMapper.map(data.originLocation)
          : null,
        data.destinationLocation
          ? RateLocationMapper.map(data.destinationLocation)
          : null,
        data.blPrice || 0,
        data.blPriceMargin,
      );
    }

    if (
      data.discriminator === rateDiscriminator.ptl ||
      data.discriminator === rateDiscriminator.ptlFlat
    ) {
      return new PtlFlatRate(
        data.id,
        data.name,
        data.discriminator,
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.currency,
        data.contractNumber,
        data.availableOnline,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.flatRateRoutes
          ? data.flatRateRoutes.map(RateFlatRouteMapper.map)
          : [],
        data.originLocation
          ? RateLocationMapper.map(data.originLocation)
          : null,
        data.destinationLocation
          ? RateLocationMapper.map(data.destinationLocation)
          : null,
        data.blPrice || 0,
        data.blPriceMargin,
        data.chargeableWeightPrice || 0,
      );
    }

    if (data.discriminator === rateDiscriminator.fclPickupDelivery) {
      return new FclPickupDeliveryRate(
        data.id,
        data.name,
        data.discriminator,
        data.rangeType,
        data.equipment ? data.equipment.map((eq) => String(eq)) : [],
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.contractNumber,
        data.availableOnline,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.currency,
        data.terminalType,
        data.direction,
        data.hiddenMarkup,
      );
    }

    if (data.discriminator === rateDiscriminator.airFlat) {
      return new AirFlatRate(
        data.id,
        data.name,
        data.discriminator,
        data.equipment ? data.equipment.map((eq) => String(eq)) : [],
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.contractNumber,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.incoterms && Array.isArray(data.incoterms) ? data.incoterms : [],
        data.flatRateRoutes
          ? data.flatRateRoutes.map(RateFlatRouteMapper.map)
          : [],
        data.totalWeightPriceLt45 || 0,
        data.totalWeightPriceGt45 || 0,
        data.totalWeightPriceGt100 || 0,
        data.totalWeightPriceGt300 || 0,
        data.totalWeightPriceGt500 || 0,
        data.totalWeightPriceGt1000 || 0,
        data.availableOnline,
        new AirWeightBracketPrices(data.weightBracketMargins),
        data.maxWeightPerShipment,
        data.maxWeightPerPiece,
        data.maxMeasurementPerShipment,
        data.maxMeasurementPerPiece,
        data.maxTaxWeight,
        data.maxLength,
        data.maxHeight,
        data.maxWidth,
        data.maxLWH,
        data.marginType,
      );
    }

    if (data.discriminator === rateDiscriminator.lcl) {
      return new LclRate(
        data.id,
        data.name,
        data.discriminator,
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.currency,
        data.weightPrice || 0,
        data.measurementPrice || 0,
        data.weightTotals || 0,
        data.measurementTotals || 0,
        data.weightUnit || lclWeightUnit.kg,
        data.minPrice || 0,
        data.blPrice || 0,
        data.availableOnline,
        data.maxWeightPerShipment,
        data.maxWeightPerPiece,
        data.maxMeasurementPerShipment,
        data.maxMeasurementPerPiece,
        data.maxTaxWeight,
        data.maxLength,
        data.maxHeight,
        data.maxWidth,
        data.maxLWH,
        new LclWeightBracketPrices(data.weightBracketMargins),
        data.originLocations && !data.baseOriginLocation
          ? RateLocationMapper.map(data.originLocations[0])
          : data.originLocation
            ? RateLocationMapper.map(data.originLocation)
            : RateLocationMapper.map(data.baseOriginLocation) || null,
        data.destinationLocations && !data.baseDestinationLocation
          ? RateLocationMapper.map(data.destinationLocations[0])
          : data.destinationLocation
            ? RateLocationMapper.map(data.destinationLocation)
            : RateLocationMapper.map(data.baseDestinationLocation) || null,
        data.contractNumber,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.incoterms && Array.isArray(data.incoterms) ? data.incoterms : [],
        data.chargeableWeight || 0,
        data.chargeableWeightPrice || 0,
        data.chargeableWeightMargin,
        data.blMargin,
        data.marginType,
        data.minDensity,
        data.maxDensity,
      );
    }

    if (data.discriminator === rateDiscriminator.lclFlat) {
      return new LclFlatRate(
        data.id,
        data.name,
        data.discriminator,
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.contractNumber,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.incoterms && Array.isArray(data.incoterms) ? data.incoterms : [],
        data.flatRateRoutes
          ? data.flatRateRoutes.map(RateFlatRouteMapper.map)
          : [],
        data.weightTotals || 0,
        data.measurementTotals || 0,
        data.blPrice || 0,
        data.availableOnline,
        data.maxWeightPerShipment,
        data.maxWeightPerPiece,
        data.maxMeasurementPerShipment,
        data.maxMeasurementPerPiece,
        data.maxTaxWeight,
        data.maxLength,
        data.maxHeight,
        data.maxWidth,
        data.maxLWH,
        new LclWeightBracketPrices(data.weightBracketMargins),
        data.marginType,
        data.minDensity,
        data.maxDensity,
      );
    }

    if (data.discriminator === rateDiscriminator.air) {
      return new AirRate(
        data.id,
        data.name,
        data.discriminator,
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.currency,
        data.weightTotals || 0,
        data.measurementTotals || 0,

        data.weightPriceLt45 || 0,
        data.weightPriceGt45 || 0,
        data.weightPriceGt100 || 0,
        data.weightPriceGt300 || 0,
        data.weightPriceGt500 || 0,
        data.weightPriceGt1000 || 0,

        data.totalWeightPriceLt45 || 0,
        data.totalWeightPriceGt45 || 0,
        data.totalWeightPriceGt100 || 0,
        data.totalWeightPriceGt300 || 0,
        data.totalWeightPriceGt500 || 0,
        data.totalWeightPriceGt1000 || 0,

        data.minPrice || 0,
        data.blPrice || 0,
        data.originLocations && !data.baseOriginLocation
          ? RateLocationMapper.map(data.originLocations[0])
          : data.originLocation
            ? RateLocationMapper.map(data.originLocation)
            : RateLocationMapper.map(data.baseOriginLocation) || null,
        data.destinationLocations && !data.baseDestinationLocation
          ? RateLocationMapper.map(data.destinationLocations[0])
          : data.destinationLocation
            ? RateLocationMapper.map(data.destinationLocation)
            : RateLocationMapper.map(data.baseDestinationLocation) || null,
        data.contractNumber,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.incoterms && Array.isArray(data.incoterms) ? data.incoterms : [],
        data.chargeableWeight || 0,
        data.chargeableWeightPrice || 0,
        data.chargeableWeightMargin,
        data.availableOnline,
        new AirWeightBracketPrices(data.weightBracketMargins),
        data.maxWeightPerShipment,
        data.maxWeightPerPiece,
        data.maxMeasurementPerShipment,
        data.maxMeasurementPerPiece,
        data.maxTaxWeight,
        data.maxLength,
        data.maxHeight,
        data.maxWidth,
        data.maxLWH,
        data.marginType,
      );
    }

    if (data.discriminator === rateDiscriminator.ltlPickupDelivery) {
      return new LtlPickupDeliveryRate(
        data.id,
        data.name,
        data.discriminator,
        data.rangeType,
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.contractNumber,
        data.availableOnline,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.flatRateRoutes
          ? data.flatRateRoutes.map(RateFlatRouteMapper.map)
          : [],
        data.currency,
        data.ldm,
        data.cbm,
        data.ldmThreshold,
        data.ldmWidthThreshold,
        data.ldmHeightThreshold,
        data.ldmLengthThreshold,
        data.lengthThreshold,
        data.lengthExtraCharge,
        data.lengthPercentageExtraCharge,
        data.lengthValueExtraCharge,
        data.minWeight,
        data.maxWeight,
        data.minTaxWeight,
        data.maxTaxWeight,
        data.maxLength,
        data.maxHeight,
        data.maxWidth,
        data.maxLW,
        data.maxLWH,
        data.minLength,
        data.minHeight,
        data.minWidth,
        data.minLW,
        data.minLWH,
        data.terminalType,
        data.direction,
        data.availableWeightBrackets
          ? data.availableWeightBrackets.map((eq) => String(eq))
          : [],
        data.hiddenMarkup,
        data.companies ? data.companies.map((c) => CompanyMapper.map(c)) : [],
      );
    }

    if (data.discriminator === rateDiscriminator.ltlLinked) {
      return new LtlLinkedRate(
        data.id,
        data.name,
        data.discriminator,
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.contractNumber,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.originLocation
          ? RateLocationMapper.map(data.originLocation)
          : null,
        data.destinationLocation
          ? RateLocationMapper.map(data.destinationLocation)
          : null,
        data.debugInfo,
        data.currency,
        data.availableWeightBrackets
          ? data.availableWeightBrackets.map((eq) => String(eq))
          : [],
        data.hiddenMarkup,
        data.companies ? data.companies.map((c) => CompanyMapper.map(c)) : [],
        <LtlPickupDeliveryRate>RateMapper.map(data.pickupRate),
        <LtlPickupDeliveryRate>RateMapper.map(data.deliveryRate),
        new WeightBracketPrices(data.weightBracketMargins),
        data.rates ? data.rates.map((r) => RateMapper.map(r)) : [],
        data.terminalOrder ? data.terminalOrder : [],
        data.chargeableWeightPrice,
        data.chargeableWeightMargin,
        data.noMarginPrice,
        data.departureDate ? new Date(data.departureDate) : null,
        data.deliveryDate ? new Date(data.deliveryDate) : null,
      );
    }

    if (data.discriminator === rateDiscriminator.ltl) {
      return new LtlRate(
        data.id,
        data.name,
        data.discriminator,
        data.createdBy ? UserMapper.map(data.createdBy) : null,
        data.updatedBy ? UserMapper.map(data.updatedBy) : null,
        data.createdAt ? new Date(data.createdAt) : null,
        data.updatedAt ? new Date(data.updatedAt) : null,
        data.type,
        data.source,
        data.status,
        data.internalNote,
        data.externalNote,
        data.pmNote,
        data.comment,
        data.deepLink,
        CarrierMapper.map(data.carrier),
        RateMapper.mapValidityDates(data),
        data.contractNumber,
        data.restrictedClients
          ? data.restrictedClients.map(ClientMapper.map)
          : [],
        data.restrictedUsers ? data.restrictedUsers.map(UserMapper.map) : [],
        data.rootParent ? RateMapper.map(data.rootParent) : null,
        data.flatRateRoutes
          ? data.flatRateRoutes.map(RateFlatRouteMapper.map)
          : [],
        data.originLocation
          ? RateLocationMapper.map(data.originLocation)
          : null,
        data.destinationLocation
          ? RateLocationMapper.map(data.destinationLocation)
          : null,
        data.debugInfo,
        data.currency,
        data.ldm,
        data.cbm,
        data.ldmThreshold,
        data.ldmWidthThreshold,
        data.ldmHeightThreshold,
        data.ldmLengthThreshold,
        data.lengthThreshold,
        data.lengthExtraCharge,
        data.lengthPercentageExtraCharge,
        data.lengthValueExtraCharge,
        data.minWeight,
        data.maxWeight,
        data.minTaxWeight,
        data.maxTaxWeight,
        data.maxLength,
        data.maxHeight,
        data.maxWidth,
        data.maxLW,
        data.maxLWH,
        data.minLength,
        data.minHeight,
        data.minWidth,
        data.minLW,
        data.minLWH,
        data.minMargin,
        data.maxMargin,
        data.availableOnline,
        data.domestic,
        data.availableWeightBrackets
          ? data.availableWeightBrackets.map((eq) => String(eq))
          : [],
        data.hiddenMarkup,
        data.companies ? data.companies.map((c) => CompanyMapper.map(c)) : [],
        RateMapper.map(data.pickupRate),
        RateMapper.map(data.deliveryRate),
        new WeightBracketPrices(data.weightBracketMargins),
        data.isInvalid,
        data.chargeableWeightPrice,
        data.chargeableWeightMargin,
        data.noMarginPrice,
        data.departureDate ? new Date(data.departureDate) : null,
        data.deliveryDate ? new Date(data.deliveryDate) : null,
      );
    }
  }

  private static mapValidityDates(data: any): [Date, Date] {
    let [validFrom, validTill] = [<Date>null, <Date>null];

    if (data.validFrom) {
      validFrom =
        data.validFrom instanceof Date
          ? data.validFrom
          : new Date(`${data.validFrom}T00:00:00.000`);
    }

    if (data.validTill) {
      validTill =
        data.validTill instanceof Date
          ? data.validTill
          : new Date(`${data.validTill}T00:00:00.000`);
    }

    if (data.validity && data.validity.length > 0) {
      [validFrom, validTill] = [
        data.validity[0] || new Date(),
        data.validity[1] || new Date(),
      ];
    }

    return [validFrom, validTill];
  }
}
